import { useQuery } from "@tanstack/react-query";
import { Clock, History as HistoryIcon } from "lucide-react";
import { useAuthStore } from "@/lib/auth";
import { authFetchJson } from "@/lib/auth-fetch";
import { EmptyState } from "@/components/v2/EmptyState";

type HistoryEntry = {
  id?: string;
  action?: string;
  actorEmail?: string | null;
  actorType?: string;
  fromValue?: string | null;
  toValue?: string | null;
  createdAt?: string;
};

type HistoryResponse = { entries?: HistoryEntry[] };

/**
 * History tab — Pencil `Ul7iC`. Vertical timeline of every action that
 * touched this booking. Reads `/api/v1/bookings/{id}/history` (added in this
 * phase to expose the existing `BookingHistory` table).
 */
export function BookingHistoryTab({ bookingId }: { bookingId: string }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["booking", bookingId, "history"],
    enabled: !!accessToken && !!bookingId,
    queryFn: () =>
      authFetchJson<HistoryResponse>(`/api/v1/bookings/${bookingId}/history`),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading history…</p>;
  if (isError)
    return <p className="text-sm text-destructive">{(error as Error).message}</p>;

  const entries = data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={HistoryIcon}
        title="No history yet"
        description="Status changes, payment events, and edits will appear here as they happen."
      />
    );
  }

  return (
    <ol className="relative pl-6">
      <span className="absolute left-2 top-2 bottom-2 w-px bg-border" aria-hidden />
      {entries.map((e) => (
        <li key={e.id} className="relative pb-5 last:pb-0">
          <span className="absolute -left-[18px] top-1 h-3 w-3 rounded-full bg-card border-2 border-primary" />
          <div className="text-sm">
            <p className="font-medium">{humanizeAction(e.action)}</p>
            {(e.fromValue || e.toValue) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {e.fromValue ? <code>{e.fromValue}</code> : "—"}
                {" → "}
                {e.toValue ? <code>{e.toValue}</code> : "—"}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>
                {e.createdAt ? new Date(e.createdAt).toLocaleString() : "—"}
                {" · "}
                {e.actorEmail ?? e.actorType ?? "system"}
              </span>
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function humanizeAction(action?: string): string {
  if (!action) return "Event";
  return (
    {
      created: "Booking created",
      status_changed: "Status changed",
      payment_received: "Payment received",
      payment_verified: "Payment verified",
      payment_rejected: "Payment rejected",
      rescheduled: "Rescheduled",
      cancelled: "Cancelled",
      edited: "Edited",
    }[action] ?? action.replace(/_/g, " ")
  );
}

